<template>
  <div class="unified-date-picker">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      min-width="290px"
    >
      <template #activator="{ props: activatorProps }">
        <!-- Single date selection input -->
        <v-text-field
          v-if="!multiple"
          v-model="singleFormattedDate"
          hide-details
          :disabled="disabled"
          :class="{ 'font-weight-bold': boldText }"
          :placeholder="defaultPlaceholder"
          v-bind="activatorProps"
          @update:model-value="updateSingleDateText"
        >
          <template #append-inner>
            <v-icon size="small" :title="iconTitle" v-bind="activatorProps">
              mdi-calendar-blank-outline
            </v-icon>
          </template>
        </v-text-field>

        <!-- Multiple date selection input -->
        <v-combobox
          v-else
          v-bind="activatorProps"
          :model-value="multipleFormattedDates"
          :disabled="disabled"
          :placeholder="defaultPlaceholder"
          multiple
          @update:model-value="handleMultipleDateInput"
        >
          <template #append-inner>
            <v-icon size="small" :title="iconTitle" v-bind="activatorProps" @click="menu = true">
              mdi-calendar-blank-outline
            </v-icon>
          </template>
        </v-combobox>
      </template>
      <!-- eslint-disable vuetify/no-deprecated-props -->
      <!-- This was fixed here, but not on lint https://github.com/vuetifyjs/vuetify/pull/20096 -->
      <v-date-picker
        :allowed-dates="adjustedAllowedDates"
        :first-day-of-week="firstDayOfWeek"
        :model-value="castDate"
        :multiple="multiple"
        hide-header
        scrollable
        @update:model-value="handleDatePickerUpdate"
      >
        <template v-if="showActions" #actions>
          <v-spacer />
          <v-btn v-if="multiple" variant="text" color="primary" @click="clearSelectedDates">
            {{ $t('actions.unselect') }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="menu = false">
            {{ $t('actions.cancel') }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="confirmSelection">
            {{ $t('actions.ok') }}
          </v-btn>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { isArray, debounce, map, range, flatMap } from 'lodash';

// Stores
import { useContextStore } from '@/stores/context';

// Composables
import { useDayjs } from '@/composables/use-dayjs';

export const DateFormats = {
  shortDate: 'YYYY-MM-DD',
  humanDateEu: 'DD-MM-YYYY',
  ISO8601: 'YYYY-MM-DDTHH:mm:ss.msZ',
};

const { ISO8601, humanDateEu: displayFormat, shortDate: parseFormat } = DateFormats;

export default {
  props: {
    // Common props
    value: {
      required: false,
      type: [String, Array],
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
    allowedDates: {
      type: [Function, Array],
      default: null,
    },
    firstDayOfWeek: {
      type: Number,
      default: 1, // Monday
    },
    iconTitle: {
      type: String,
      default: 'Date Picker',
    },

    // Single date specific props
    boldText: {
      type: Boolean,
      default: false,
    },
    displayDateFormat: {
      type: String,
      default: displayFormat,
    },

    // Multiple date specific props
    multiple: {
      type: Boolean,
      default: false,
    },
    enableWeekSelection: {
      type: Boolean,
      default: false,
    },
    mondaysOnly: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue'],

  setup() {
    const { dayjs, formatUtcDate, getTzZeroedDate, getUTCNow } = useDayjs();
    const { getDateFormats } = useContextStore();
    const defaultFormat = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

    return {
      defaultFormat,
      // useDayjs
      dayjs,
      formatUtcDate,
      getTzZeroedDate,
      getUTCNow,

      // useContextStore
      getDateFormats,
    };
  },

  data() {
    return {
      menu: false,
      singleFormattedDate: '',
      isTyping: false,
      expectedFormats: [ISO8601, displayFormat, parseFormat],
    };
  },

  computed: {
    castDate() {
      if (this.multiple) return this.castMultipleDates;

      return this.castSingleDate;
    },

    defaultPlaceholder() {
      return this.placeholder || this.$t('workpackagePage.scope.datesLabel');
    },

    castMultipleDates() {
      return (this.value || []).map(date => {
        // Parse UTC date and create local JS date preserving the date
        const utcDate = this.dayjs.utc(date);
        return new Date(utcDate.year(), utcDate.month(), utcDate.date());
      });
    },

    castSingleDate() {
      if (!this.value) return null;
      // Parse UTC date and create local JS date preserving the date
      const utcDate = this.dayjs.utc(this.value);
      return new Date(utcDate.year(), utcDate.month(), utcDate.date());
    },

    adjustedAllowedDates() {
      if (isArray(this.allowedDates)) {
        return this.allowedDates.map(this.getTzZeroedDate);
      }
      return this.allowedDates;
    },

    multipleFormattedDates() {
      if (!this.multiple || !this.value) return [];

      let dates = this.value;

      // Filter for Mondays only if enabled
      if (this.mondaysOnly) {
        dates = dates.filter(date => this.dayjs.utc(date).isoWeekday() === 1);
      }

      // Format dates for display
      const dateFormat = this.getDateFormats?.long || this.displayDateFormat;
      return dates.map(date => this.dayjs.utc(date).format(dateFormat));
    },
  },

  watch: {
    value: {
      handler(newValue) {
        if (!this.multiple && newValue && this.isValidDate(newValue) && !this.isTyping) {
          this.singleFormattedDate = this.displayFormatting(newValue);
        }
      },
      immediate: true,
    },
  },

  created() {
    if (!this.multiple && this.value && this.isValidDate(this.value)) {
      this.singleFormattedDate = this.displayFormatting(this.value);
    }
    this.debouncedSetTypingInactive = debounce(() => {
      this.isTyping = false;
    }, 1000);
  },

  methods: {
    // Single date methods
    displayFormatting(date) {
      if (!date) return null;
      const utcDate = this.dayjs.utc(date);
      return utcDate.format(this.displayDateFormat);
    },

    parseDate(date) {
      if (!date) return null;
      // Parse the date in the display format and convert to UTC preserving the date
      const localDate = this.dayjs(date, this.displayDateFormat).startOf('day');
      const utcDate = this.dayjs.utc({
        year: localDate.year(),
        month: localDate.month(),
        date: localDate.date(),
      });
      return utcDate.format(this.defaultFormat);
    },

    updateSingleDateText(date) {
      this.setTypingActive();
      if (this.isValidDate(date)) {
        const parsedDate = this.parseDate(date);
        this.emitValue(parsedDate);
      }
      this.debouncedSetTypingInactive();
    },

    handleMultipleDateInput(dates) {
      if (this.enableWeekSelection) {
        this.selectWeeks(dates);
      } else {
        this.selectRegularDates(dates);
      }
    },

    selectWeeks(dates) {
      const mondays = dates.filter(date => this.dayjs(date).day() === 1);

      // Generate week arrays for each Monday, convert to UTC preserving the date
      const getUtcWeek = date => {
        return map(range(7), i => {
          // Use startOf('day') to ensure we preserve the date regardless of timezone
          const localDate = this.dayjs(date).add(i, 'day').startOf('day');
          const utcDate = this.dayjs.utc({
            year: localDate.year(),
            month: localDate.month(),
            date: localDate.date(),
          });
          return utcDate.format(this.defaultFormat);
        });
      };

      const exclusions = flatMap(mondays, getUtcWeek);
      this.emitValue(exclusions);
    },

    selectRegularDates(dates) {
      const utcDates = dates.map(date => {
        const localDate = this.dayjs(date).startOf('day');
        // Convert to UTC while preserving the date by using the date components
        const utcDate = this.dayjs.utc({
          year: localDate.year(),
          month: localDate.month(),
          date: localDate.date(),
        });
        return utcDate.format(this.defaultFormat);
      });
      this.emitValue(utcDates);
    },

    // Common methods
    handleDatePickerUpdate(selectedDates) {
      if (this.multiple) {
        this.handleMultipleDateInput(selectedDates);
      } else {
        if (this.isValidDate(selectedDates)) {
          this.setTypingActive();
          // For single date, preserve the date by using date components
          const localDate = this.dayjs(selectedDates).startOf('day');
          const utcDate = this.dayjs.utc({
            year: localDate.year(),
            month: localDate.month(),
            date: localDate.date(),
          });
          const utcDateString = utcDate.format(this.defaultFormat);
          this.singleFormattedDate = this.displayFormatting(utcDateString);
          this.emitValue(utcDateString);
        }
        if (!this.showActions) {
          this.menu = false;
        }
      }
    },

    confirmSelection() {
      this.menu = false;
    },

    clearSelectedDates() {
      this.setTypingInactive();
      this.emitValue(this.multiple ? [] : null);
      if (!this.multiple) {
        this.singleFormattedDate = '';
      }
    },

    emitValue(value) {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
    },

    setTypingActive() {
      this.isTyping = true;
    },

    setTypingInactive() {
      this.isTyping = false;
    },

    isValidDate(date) {
      return this.dayjs(date).isValid();
    },
  },
};
</script>

<style lang="scss" scoped>
.unified-date-picker {
  min-width: 180px;
  :deep(.v-combobox) {
    .v-field__field {
      height: auto !important;
    }
  }

  :deep(.v-input__control) {
    max-width: 223px;
  }
}
</style>
